import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Avatar,
    Button,
    useMediaQuery
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import SwanLogo from "../../assets/logos/swansolutions_logo.png";
import { useSiteContent } from "../../services/content";

// Testimonials data
const testimonials = [
    {
        name: "Emily Thornton",
        title: "Thomasville City Schools",
        image:
            "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/130d17b2-f8fe-4e84-ac3c-4bf0728bb5f8_20241213153031.png",
        quote:
            "FIG K-12 is a very efficient child nutrition professional development tool. I have found the FIG K-12 to be one of our school food service best purchases. The program streamlines the required school food service professional development requirements. The program furnishes on demand training videos on a variety of topics. The topic includes HACCP, Food Safety, operational strategies, and more. The reports make professional development tracking simple and organized. Highly recommend FIG K-12!",
    },
    {
        name: "Julie Fuerch",
        title: "Barker Central and Royalton Hartland Central Schools",
        image:
            "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/bad62c6a-3b22-402d-911d-5e1edf7b9927_20241213152144.png",
        quote:
            "At Barker Central School and Royalton Hartland Central School, we've seen significant changes over the years. The days of long-tenured staff who instinctively knew our kitchen routines are behind us. Like many of my colleagues in school nutrition, we now face the challenge of a revolving door of employees who may not have the deep-rooted experience we once relied on. While we�re grateful for the new team members who bring energy and new ideas, training is now a continuous process. FIG K12 has made it much easier to onboard, train, and keep everyone up to date. It�s become a critical part of our department�s success.",
    },
    {
        name: "Quentin Cavanagh",
        title: "Anderson School District 5",
        image:
            "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/4f6030b1-3f1b-4bd8-94b2-ce4b4700c24b_20241213153236.png",
        quote:
            "We are on our 2nd full school year with Swan Solutions and the Fig product and like the program very much. On the back end it is very easy to set up Professional Development tasks and the reports are simple to access and understand. On the front side the program is very user friendly and the fact that it is web-based makes it very flexible for our staff. Our district uses Swan Solutions for both tracking training and reporting CEUs. The support team has been responsive and open to suggestions for improvement, which is much appreciated.",
    },
    {
        name: "Scott Ziobrowski",
        title: "Hilton Central School District",
        image:
            "https://api.figk-12.com/Files/WhatWeDoBoxes/Image/375f8a72-8783-4ce5-9af3-d0879d63b2f9_20241218115132.png",
        quote:
            "I would like to take the time out to thank your company for creating an amazing product with FIG K12. As a 14 year Food Service Director, it is important to streamline and utilize time management to the best of our abilities, and your software has allowed us to do that. The tracking of CEU�s and the ability to categorize them with ease, has allowed us to not only have a clean audit trail, but also maximize our time and focus more on supporting our students and staff.",
    },
];

const variants = {
    enter: (direction) => ({
        x: direction > 0 ? 300 : -300,
        opacity: 0,
        position: "absolute",
    }),
    center: { x: 0, opacity: 1, position: "static" },
    exit: (direction) => ({
        x: direction > 0 ? -300 : 300,
        opacity: 0,
        position: "absolute",
    }),
};

export default function Testimonials() {
    const content = useSiteContent();
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    const handleNext = () => {
        setDirection(1);
        setIndex((prev) => (prev + 1) % testimonials.length);
    };

    const handlePrev = () => {
        setDirection(-1);
        setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const t = testimonials[index];

    return (
        <Box sx={{ bgcolor: "#f7fafb", minHeight: "100vh" }}>
            {/* HERO */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "30vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Box
                    component="img"
                    src={SwanLogo}
                    alt="Swan Solutions Logo"
                    sx={{
                        height: { xs: 70, md: 110 },
                        mb: 3,
                        filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.12))",
                    }}
                />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        mb: 2,
                        fontSize: { xs: "2rem", md: "3rem" },
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    {content("testimonials.hero.title", "Real Impact, Real Voices")}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        maxWidth: 800,
                        mx: "auto",
                        mb: 3,
                        color: "rgba(255,255,255,0.97)",
                        fontWeight: 400,
                        fontSize: { xs: "1.1rem", md: "1.2rem" },
                        textShadow: "0 2px 12px rgba(40,120,140,0.08)",
                    }}
                >
                    {content("testimonials.hero.subtitle", "Hear from school nutrition leaders using Swan Solutions and FIG K12 to transform their operations.")}
                </Typography>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: { xs: 1, sm: 3 },
                    }}
                >
                    <Button
                        onClick={handlePrev}
                        sx={{
                            minWidth: 0,
                            px: 0.5,
                            color: "#208478",
                            bgcolor: "rgba(33,219,184,0.08)",
                            borderRadius: "50%",
                            "&:hover": { bgcolor: "#e7f9f6" },
                            boxShadow: "0 2px 8px 0 rgba(20,76,100,0.13)",
                        }}
                        aria-label="Previous testimonial"
                    >
                        <ArrowBackIos sx={{ fontSize: 34 }} />
                    </Button>

                    <Box sx={{ width: "100%", maxWidth: 530, minHeight: 470, position: "relative", mx: "auto" }}>
                        <AnimatePresence initial={false} custom={direction}>
                            <motion.div
                                key={index}
                                custom={direction}
                                variants={variants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{
                                    x: { type: "spring", stiffness: 260, damping: 22 },
                                    opacity: { duration: 0.24 }
                                }}
                                style={{ width: "100%", position: "absolute" }}
                            >
                                <Card
                                    sx={{
                                        borderRadius: 4,
                                        boxShadow: 6,
                                        minHeight: { xs: 370, md: 470 },
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        px: { xs: 2, md: 6 },
                                        py: { xs: 3, md: 5 },
                                    }}
                                >
                                    <Avatar
                                        src={t.image}
                                        alt={t.name}
                                        sx={{
                                            width: { xs: 144, md: 170 },
                                            height: { xs: 144, md: 170 },
                                            mb: 3,
                                            border: "5px solid #21dbb8",
                                            boxShadow: "0 2px 24px #21dbb830",
                                            objectFit: "cover"
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 800,
                                            mt: 1,
                                            mb: 0.5,
                                            color: "primary.main",
                                            textAlign: "center",
                                            fontSize: { xs: "1.2rem", md: "1.35rem" }
                                        }}
                                    >
                                        {t.name}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ color: "text.secondary", mb: 2, textAlign: "center" }}
                                    >
                                        {t.title}
                                    </Typography>
                                    <CardContent sx={{ p: 0 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: "text.primary",
                                                fontSize: { xs: "1.13rem", md: "1.16rem" },
                                                fontStyle: "italic",
                                                textAlign: "center",
                                                minHeight: { xs: 120, md: 160 }
                                            }}
                                        >
                                            {t.quote}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </AnimatePresence>
                    </Box>

                    <Button
                        onClick={handleNext}
                        sx={{
                            minWidth: 0,
                            px: 0.5,
                            color: "#208478",
                            bgcolor: "rgba(33,219,184,0.08)",
                            borderRadius: "50%",
                            "&:hover": { bgcolor: "#e7f9f6" },
                            boxShadow: "0 2px 8px 0 rgba(20,76,100,0.13)",
                        }}
                        aria-label="Next testimonial"
                    >
                        <ArrowForwardIos sx={{ fontSize: 34 }} />
                    </Button>
                </Box>
                {/* Dots */}
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    {testimonials.map((_, i) => (
                        <Box
                            key={i}
                            onClick={() => {
                                setDirection(i > index ? 1 : -1);
                                setIndex(i);
                            }}
                            sx={{
                                width: i === index ? 22 : 12,
                                height: 12,
                                borderRadius: 99,
                                bgcolor: i === index ? "#21dbb8" : "#c6e9e0",
                                mx: 0.5,
                                cursor: "pointer",
                                transition: "all .2s"
                            }}
                        />
                    ))}
                </Box>
            </Container>
        </Box>
    );
}
